/**
 * FingeringEditorPageMobile (issue #17 Phase 4.2)
 *
 * モバイル専用レイアウト: flex-col、 bottom sheet で EditorPanel を提示
 * (translate-y アニメ + 背景オーバーレイ)。
 * 案 C-2 採用のため、 wrapper (FingeringEditorPage) で `useFingeringEditor()`
 * を 1 度呼び出し、 結果を props `editor` で受け取る。
 *
 * Phase 4.2 commit ではテスト追加・実 wrapper 切替は次 commit (4.3) で実施。
 */
import React from 'react';
import { Link } from 'react-router-dom';
import BadgeSvg from './components/BadgeSvg';
import EditorPanel from './components/EditorPanel';
import EditorToolbar from './components/EditorToolbar';
import DiscardConfirmModal from './components/DiscardConfirmModal';
import ReauthRequiredModal from './components/ReauthRequiredModal';
import RepeatEditConfirmModal from './components/RepeatEditConfirmModal';
import ChordNotePicker from './components/ChordNotePicker';
import DragDebugOverlay from './components/editor/DragDebugOverlay';
import { dragDebug } from './components/editor/dragDebug';
import EditorHeader from './components/editor/EditorHeader';
import EditorPageNav from './components/editor/EditorPageNav';
import EditorToastBubble from './components/editor/EditorToastBubble';
import EditorSvgCanvas, { SVG_WIDTH, SVG_HEIGHT } from './components/editor/EditorSvgCanvas';
import SelectRectOverlay from './components/editor/SelectRectOverlay';
import PlacementGhost from './components/editor/PlacementGhost';
import PlacementCaptureOverlay from './components/editor/PlacementCaptureOverlay';
import { isDragTarget } from '../../utils/dragTarget';
import type { UseFingeringEditorReturn } from '../../hooks/useFingeringEditor';
import type { UseFingeringClipboardReturn } from '../../hooks/useFingeringClipboard';

interface FingeringEditorPageMobileProps {
  editor: UseFingeringEditorReturn;
  clipboard: UseFingeringClipboardReturn;
}

const FingeringEditorPageMobile: React.FC<FingeringEditorPageMobileProps> = ({ editor, clipboard }) => {
  const {
    badges, fingeringData, pageBadges, chordGroups, measureLines, stats, selected, canUndo,
    // issue #166: 繰り返しグループの一時ハイライト対象 id 集合
    highlightedBadgeIds,
  } = editor.data;
  const {
    zoom, pan, zoomOrigin, isMobile, isPanDragging, currentPage,
    pageCount, pdfDataUrl, toast, loading,
  } = editor.view;
  const {
    editorMode, addMode, panelOpen,
    // issue #16 Phase 8b: 範囲選択 / v3 Phase A: selectMode toggle 撤廃済 (Core 派生)
    // Phase H-2: selectRectVisible を destructure 追加 (Desktop と同じ矩形 UI 描画のため)
    // issue #45 バグ B: selectMode を destructure 追加 (Mobile + edit で sticky bar を出さないマスク用)。
    selectedIds, isMultiSelect, selectedCount, selectRectVisible, selectMode, storedMode,
  } = editor.mode;
  // issue #26 Phase 5b (plan v5.1 §2.3): sticky bar を Mobile page 直下 (画面下部 fixed) に移設したため、
  //   EditorToolbar の selectedCount props は常に 0 で OK (Mobile では selectionBarEl 経路自体が削除済)。
  //   sticky bar の表示条件は下部 JSX で `selectMode && selectedCount > 0` を直接判定する (#45 バグ B マスク自然継承)。
  const { dragBadgeId, dragDelta } = editor.drag;
  const {
    chordPickerBeatIndex, chordPickerPos,
    detectedBpm, currentBpm, setCurrentBpm,
    tuningChangeOpen,
    // issue #166: 繰り返し編集の確認モーダル保留 state
    repeatEditConfirm,
  } = editor.edit;
  const {
    isGenerating, isGeneratingAudio,
    // issue #165: 投入前 Drive 再ログインゲート
    reauthModalOpen, handleReauthConfirm, handleReauthCancel,
  } = editor.regenerate;
  const {
    svgRef, svgWrapperRef, scrollContainerRef, dragRef, editorModeRef,
  } = editor.refs;
  const {
    handleUndo,
    handleTouchStart, handleTouchEnd,
    dismissPanel, handlePageChange,
    handleSvgPointerDown, handleSvgClick, handleBadgeDragStart,
    handlePointerMove, handlePointerUp, handlePointerLeave,
    addNoteToBeat, handleChordNoteSelect, handleChordAddNote, handleChordPickerClose,
    handleSave, handleDelete,
    // issue #166: 繰り返し編集の確認モーダル分岐
    confirmRepeatIndividual, confirmRepeatAll,
    handleSaveAndRegenerateAll, handleGeneratePdfOnly, handleGenerateAudioOnly,
    handleTuningChangeRegenerate, handleConfirmDiscard, handleCancelDiscard,
    handleToggleAddMode, handleEditorModeChange, handleToolbarZoom,
    // issue #16 Phase 8b: 範囲削除 + clearSelection
    handleDeleteMany, clearSelection,
  } = editor.handlers;

  if (loading) {
    return <div className="fixed inset-0 bg-slate-900 flex items-center justify-center text-slate-400">読み込み中...</div>;
  }

  if (!fingeringData) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-slate-400 gap-4">
        <p>運指データがありません</p>
        <Link to="/fingering" className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800">← 運指ページに戻る</Link>
      </div>
    );
  }

  // ドラッグ中バッジの beatIndex を 1 回だけ解決する (chord-mate のドラッグ追従判定、 map 内 O(n²) 回避)。
  const dragBeatIndex = dragBadgeId !== null
    ? badges.find((x) => x.id === dragBadgeId)?.beatIndex
    : undefined;

  return (
    <div data-testid="editor-page-mobile" className="fixed inset-0 bg-slate-900 text-slate-200 flex flex-col overflow-hidden overscroll-none font-rounded">
      <EditorHeader title={fingeringData.meta.title} part={fingeringData.meta.part} mode={storedMode} stats={stats} />

      <EditorToolbar addMode={addMode} onToggleAddMode={handleToggleAddMode} zoom={zoom} onZoomChange={handleToolbarZoom}
        onSaveAndRegenerate={handleSaveAndRegenerateAll} isGenerating={isGenerating}
        isMobile={isMobile} editorMode={editorMode} onEditorModeChange={handleEditorModeChange}
        canUndo={canUndo} onUndo={handleUndo}
        currentBpm={currentBpm}
        detectedBpm={detectedBpm}
        onBpmChange={setCurrentBpm}
        bpmDirty={currentBpm !== (detectedBpm ?? 120)}
        onGeneratePdfOnly={handleGeneratePdfOnly}
        onGenerateAudioOnly={handleGenerateAudioOnly}
        isGeneratingAudio={isGeneratingAudio}
        currentScordatura={fingeringData?.meta.scordatura ?? ''}
        onTuningChangeRegenerate={handleTuningChangeRegenerate}
        // issue #16 v3 Phase A: selectMode toggle 配線は撤廃済 (Core 派生値)。
        // #26 Phase 5b: Mobile では sticky bar が Mobile page 直下に移設、 toolbar 経路の selectionBarEl は無効。
        //   selectedCount=0 で渡しても EditorToolbar 側で `!isMobile && selectedCount > 0` で守られている。
        selectedCount={0}
        onDeleteMany={() => handleDeleteMany(Array.from(selectedIds))}
        onClearSelection={clearSelection}
        // issue #168: 運指コピペを toolbar に統合 (ペースト=addButton 隣 / 配置中=インライン案内)。
        //   コピーは Mobile では下部 sticky delete bar 側に統合するため toolbar には渡さない
        //   (toolbar の copy ボタンは selectionBarEl = !isMobile && selectedCount>0 でしか出ず Mobile では死に線)。
        hasClipboard={clipboard.hasClipboard}
        placementActive={clipboard.placementActive}
        onPaste={clipboard.startPlacement}
        onCancel={clipboard.cancelPlacement} />
      <DiscardConfirmModal
        isOpen={tuningChangeOpen}
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />
      <ReauthRequiredModal
        isOpen={reauthModalOpen}
        onConfirm={handleReauthConfirm}
        onCancel={handleReauthCancel}
      />
      <RepeatEditConfirmModal
        open={!!repeatEditConfirm}
        onIndividual={confirmRepeatIndividual}
        onAll={confirmRepeatAll}
      />

      <EditorPageNav currentPage={currentPage} pageCount={pageCount} onChangePage={handlePageChange} />

      {/* issue #168: 運指コピペ操作 (ペースト/配置キャンセル) は EditorToolbar に統合済。 コピーは下部の sticky delete bar に統合 (独立バー廃止)。 */}

      <div data-testid="editor-mobile-content" className="flex-1 flex flex-col overflow-hidden relative">
        <EditorSvgCanvas
          scrollContainerRef={scrollContainerRef}
          svgWrapperRef={svgWrapperRef}
          svgRef={svgRef}
          zoom={zoom}
          pan={pan}
          zoomOrigin={zoomOrigin}
          isPanDragging={isPanDragging}
          dragBadgeId={dragBadgeId}
          addMode={addMode}
          editorMode={editorMode}
          isMobile={isMobile}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onSvgClick={handleSvgClick}
          onSvgPointerDown={handleSvgPointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onPointerCancel={(e) => {
            // TODO: REMOVE AFTER #27 DEBUG — pointercancel は本件の最重要ポイント
            dragDebug(
              `CANCEL type=${e.pointerType} phase=${dragRef.current?.phase ?? 'null'} mode=${editorModeRef.current}`,
              { highlight: true },
            );
            handlePointerLeave();
          }}
        >
          <rect width={SVG_WIDTH} height={SVG_HEIGHT} fill="#fefce8" rx="4" />
          {pdfDataUrl && (
            <image href={pdfDataUrl} x="0" y="0" width={SVG_WIDTH} height={SVG_HEIGHT} preserveAspectRatio="xMidYMid meet" />
          )}
          {measureLines.map((ml, i) => (
            <g key={`ml-${i}`}>
              <line data-testid="measure-line" data-measure-line x1={ml.x} y1={ml.y1} x2={ml.x} y2={ml.y2}
                stroke="#94a3b8" strokeWidth={0.5} strokeDasharray="3 2" opacity={0.5} />
              <text data-testid="measure-label" data-measure-label x={ml.x} y={ml.y1 - 3}
                textAnchor="middle" fontSize={6} fill="#94a3b8" opacity={0.6}>
                m{ml.measure}
              </text>
            </g>
          ))}
          {pageBadges.map((b) => {
            const chord = b.beatIndex !== null ? chordGroups.get(b.beatIndex) : undefined;
            const isDragging = dragBadgeId !== null
              && isDragTarget(b, dragBadgeId, dragBeatIndex ?? null, selectedIds);
            // issue #16 Phase 8b: selectionState 計算 (Desktop と同じ)。
            // selectMode 中のタップは Mobile hook (Phase 6) が handleBadgeDragStart 内で toggle 呼出するため
            // page 側で onPointerDown を wrap する必要はない (Desktop の Ctrl+クリック additive は wrap)。
            const isInSelection = selectedIds.has(b.id);
            const selectionState: 'none' | 'primary' | 'multi' = isInSelection
              ? (isMultiSelect ? 'multi' : 'primary')
              : 'none';
            return <BadgeSvg key={b.id} badge={b} chordNotes={chord}
              isSelected={isInSelection}
              selectionState={selectionState}
              isHighlighted={highlightedBadgeIds.has(b.id)}
              isDragging={isDragging}
              dragDelta={isDragging ? dragDelta : null}
              applyTouchOffset={isMobile}
              onPointerDown={handleBadgeDragStart} />;
          })}
          {/* issue #16 Phase H-2 / spec §6.5: 範囲選択 drag 中の矩形 overlay。 Desktop と同 component (DRY)。 */}
          <SelectRectOverlay selectRectVisible={selectRectVisible} currentPage={currentPage} />
          {/* issue #168: 配置モードのゴースト (SVG 子、pointer-events 無効)。 */}
          <PlacementGhost items={clipboard.ghostItems} pos={clipboard.placementActive ? clipboard.ghostPos : null} />
        </EditorSvgCanvas>

        {/* モバイル専用 bottom sheet (panelOpen で translate-y アニメ開閉) */}
        {panelOpen && <div data-testid="editor-mobile-backdrop" onPointerDown={dismissPanel} className="fixed inset-0 bg-black/40 z-20" />}
        <div data-testid="editor-mobile-bottom-sheet" className={`fixed left-0 right-0 bottom-0 bg-slate-800 border-t border-slate-600 rounded-t-[20px] px-5 pt-3 pb-7 z-30 max-h-[65vh] overflow-y-auto
          transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${panelOpen ? 'translate-y-0' : 'translate-y-full'}`}>
          <div className="flex justify-center mb-3"><div className="w-9 h-1 rounded-sm bg-slate-600" /></div>
          <EditorPanel
            badge={selected}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={dismissPanel}
            onAddNoteToBeat={addNoteToBeat}
            chordSizeForBeat={(bi) => chordGroups.get(bi)?.length ?? 1}
          />
        </div>

        {/* issue #168: 配置モードの入力捕捉レイヤ (content 全域を覆う。scroll container の touch 経路と非干渉)。 */}
        <PlacementCaptureOverlay
          active={clipboard.placementActive}
          onPointerMove={clipboard.handlePlacementPointerMove}
          onPointerDown={clipboard.handlePlacementPointerDown}
          onPointerUp={clipboard.handlePlacementPointerUp}
          onPointerCancel={clipboard.handlePlacementPointerCancel}
        />
      </div>

      {/* issue #26 Phase 5b (plan v5.1 §2.1): Mobile 一括削除 sticky bar (画面下部 fixed)。
          表示条件 = `selectMode && selectedCount > 0` (#45 バグ B マスク自然継承、 edit では出ない)。
          z-25: bottom sheet overlay (z-20) より上、 bottom sheet 本体 (z-30) より下 → bottom sheet 展開時は本体に隠れる (UX 意図通り)。 */}
      {selectMode && selectedCount > 0 && (
        <div
          data-testid="mobile-sticky-delete-bar"
          className="fixed left-0 right-0 bottom-0 bg-slate-800/95 backdrop-blur-sm border-t border-amber-500/60 px-3 py-2 z-25 flex items-center justify-center gap-2"
        >
          <span aria-live="polite" className="text-amber-300 text-[13px] font-semibold">
            {selectedCount} 個選択
          </span>
          <button
            type="button"
            onClick={() => handleDeleteMany(Array.from(selectedIds))}
            aria-label="選択中の運指を一括削除"
            className="px-3 py-1 rounded-md border border-amber-500/60 bg-amber-600/20 text-amber-200 hover:bg-amber-600/40 hover:text-white text-[13px] font-semibold transition-colors"
          >
            🗑 一括削除
          </button>
          {/* issue #168: 選択中の運指をコピー (ClipboardActionBar から sticky bar へ統合、 二重バー解消)。 */}
          <button
            type="button"
            data-testid="clipboard-copy-button"
            onClick={clipboard.copySelection}
            aria-label="選択中の運指をコピー"
            className="px-3 py-1 rounded-md border border-amber-500/60 bg-amber-600/20 text-amber-200 hover:bg-amber-600/40 hover:text-white text-[13px] font-semibold transition-colors"
          >
            📋 コピー
          </button>
          <button
            type="button"
            onClick={clearSelection}
            aria-label="選択を解除"
            className="px-3 py-1 rounded-md border border-slate-500 bg-slate-700/40 text-slate-300 hover:bg-slate-600 text-[13px] font-semibold transition-colors"
          >
            ✕ 解除
          </button>
        </div>
      )}

      {chordPickerBeatIndex !== null && (() => {
        const group = chordGroups.get(chordPickerBeatIndex);
        if (!group) return null;
        return (
          <ChordNotePicker
            chordNotes={group}
            position={chordPickerPos}
            onSelectNote={handleChordNoteSelect}
            onAddNote={handleChordAddNote}
            onClose={handleChordPickerClose}
            showAddOption={true}
          />
        );
      })()}

      <EditorToastBubble message={toast} />

      {/* TODO: REMOVE AFTER #27 DEBUG */}
      <DragDebugOverlay />
    </div>
  );
};

export default FingeringEditorPageMobile;
